/**
 * Query + mutation hooks for scheduled catalog operations.
 *
 * Invalidation rule for every mutation: target
 * `queryKeys.catalogOperations.schedules.all`, never just the list. Pausing or
 * deleting a schedule changes the rows AND the server-computed ribbon, and
 * refreshing one without the other leaves the metrics visibly disagreeing with
 * the table underneath them.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  createSchedule,
  deleteSchedule,
  getScheduleDetail,
  getScheduleRunFeed,
  getScheduleRuns,
  getSchedules,
  getSchedulesOverview,
  getSchedulesRibbon,
  pauseSchedule,
  previewSchedule,
  resumeSchedule,
  triggerSchedule,
  updateSchedule,
  updateScheduleConfig,
  type ScheduleRunFeedFilters,
} from '../services/catalog-ops-schedule.service';
import type {
  CreateScheduleRequestWire,
  ScheduleSpecWire,
  UpdateScheduleConfigRequestWire,
  UpdateScheduleRequestWire,
} from '../types/catalog-ops-schedule.api.types';
import type { ScheduleListFilters } from '../types/catalog-ops-schedule.types';

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** Paged schedule rows. `enabled` lets the collapsed section skip the request
 *  until it is opened — nothing on the page needs the rows before that. */
export function useSchedules(filters: ScheduleListFilters, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.schedules.list(filters as Record<string, unknown>),
    queryFn: () => getSchedules(filters),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** Ribbon aggregates. Keyed on the same filters as `useSchedules` so a filter
 *  change refetches both together and the numbers never describe a different
 *  filter than the table. */
export function useSchedulesRibbon(filters: ScheduleListFilters, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.schedules.ribbon(filters as Record<string, unknown>),
    queryFn: () => getSchedulesRibbon(filters),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** Dashboard rollup — counts, the 30-day window, upcoming runs and the
 *  server-authored attention reasons. */
export function useSchedulesOverview(enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.schedules.overview(),
    queryFn: () => getSchedulesOverview(),
    staleTime: 30_000,
    enabled,
  });
}

/** One schedule's durable row plus its live Temporal block. */
export function useScheduleDetail(scheduleId: string | null) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.schedules.detail(scheduleId ?? ''),
    queryFn: () => getScheduleDetail(scheduleId ?? ''),
    enabled: !!scheduleId,
  });
}

/** Run history for one schedule, newest-first. */
export function useScheduleRuns(scheduleId: string | null, size?: number) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.schedules.scheduleRuns(scheduleId ?? ''),
    queryFn: () => getScheduleRuns(scheduleId ?? '', size === undefined ? {} : { size }),
    enabled: !!scheduleId,
  });
}

/** Cross-schedule run feed. */
export function useScheduleRunFeed(filters: ScheduleRunFeedFilters, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.schedules.runs(filters as Record<string, unknown>),
    queryFn: () => getScheduleRunFeed(filters),
    placeholderData: keepPreviousData,
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------

/**
 * Dry-runs a spec against a job. A mutation rather than a query: it's a POST,
 * and the wizard fires it deliberately when the user moves between steps rather
 * than on every keystroke of the cadence form.
 *
 * Errors are surfaced by the wizard against the offending field, so this one
 * deliberately raises no toast.
 */
export function useSchedulePreview() {
  return useMutation({
    mutationFn: ({ jobId, spec }: { jobId: string; spec?: ScheduleSpecWire }) =>
      previewSchedule(jobId, spec),
  });
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * Creates a schedule. Deliberately raises no error toast: every documented
 * failure here (400 cadence, 404 job, 409 duplicate, 422 ineligible source) is
 * a different message shown inside the wizard, against the control that caused
 * it. The caller reads `error` for that.
 */
export function useCreateSchedule() {
  const { t } = useTranslation('catalog-operations');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateScheduleRequestWire) => createSchedule(payload),
    onSuccess: async (schedule) => {
      toast.success(t('schedules.toast.created', { name: schedule.name }));
      await queryClient.invalidateQueries({
        queryKey: queryKeys.catalogOperations.schedules.all,
      });
    },
  });
}

/** Replaces a schedule's cadence, name, overlap policy and catch-up window. */
export function useUpdateSchedule(scheduleId: string) {
  const { t } = useTranslation('catalog-operations');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateScheduleRequestWire) => updateSchedule(scheduleId, payload),
    onSuccess: async () => {
      toast.success(t('schedules.toast.cadenceUpdated'));
      await queryClient.invalidateQueries({
        queryKey: queryKeys.catalogOperations.schedules.all,
      });
    },
  });
}

/** Edits the frozen config snapshot. Takes effect on the next fire — a run
 *  already in flight is unaffected. */
export function useUpdateScheduleConfig(scheduleId: string) {
  const { t } = useTranslation('catalog-operations');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateScheduleConfigRequestWire) =>
      updateScheduleConfig(scheduleId, payload),
    onSuccess: async () => {
      toast.success(t('schedules.toast.configUpdated'));
      await queryClient.invalidateQueries({
        queryKey: queryKeys.catalogOperations.schedules.all,
      });
    },
  });
}

/**
 * The row-level lifecycle actions, as one hook: the buttons sit together, share
 * one invalidation target, and only differ by which call they make.
 *
 * A 409 from pause/resume can be transient — mutating a momentarily unreachable
 * schedule returns 409 and resolves itself — so those messages invite a retry
 * rather than reading as a hard conflict.
 */
export function useScheduleLifecycle() {
  const { t } = useTranslation('catalog-operations');
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.catalogOperations.schedules.all });

  const pause = useMutation({
    mutationFn: (scheduleId: string) => pauseSchedule(scheduleId),
    onSuccess: async () => {
      toast.success(t('schedules.toast.paused'));
      await invalidate();
    },
    onError: (err) => {
      toast.error(extractApiError(err, t('schedules.toast.pauseFailed')));
    },
  });

  const resume = useMutation({
    mutationFn: (scheduleId: string) => resumeSchedule(scheduleId),
    onSuccess: async () => {
      toast.success(t('schedules.toast.resumed'));
      await invalidate();
    },
    onError: (err) => {
      toast.error(extractApiError(err, t('schedules.toast.resumeFailed')));
    },
  });

  const runNow = useMutation({
    mutationFn: (scheduleId: string) => triggerSchedule(scheduleId),
    onSuccess: async () => {
      toast.success(t('schedules.toast.runStarted'));
      await invalidate();
    },
    onError: (err) => {
      toast.error(extractApiError(err, t('schedules.toast.runFailed')));
    },
  });

  const remove = useMutation({
    mutationFn: (scheduleId: string) => deleteSchedule(scheduleId),
    onSuccess: async () => {
      toast.success(t('schedules.toast.archived'));
      await invalidate();
    },
    onError: (err) => {
      toast.error(extractApiError(err, t('schedules.toast.archiveFailed')));
    },
  });

  return { pause, resume, runNow, remove };
}
